import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import { Card, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import Pagination from '@material-ui/lab/Pagination';
import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import { Button as ChakraButton, IconButton } from '@chakra-ui/react';
import { FaHistory, FaPlusCircle, FaSearch, FaFolderOpen, FaEdit, FaTimesCircle } from 'react-icons/fa';

import FolderTreePicker from './FolderTreePicker';

import { ApplicationState } from '../../../store';
import { CompanyMaster } from '../../../models/CompanyMaster';
import { useAdminContext } from '../../../context/adminProvider';
import { logAdminAction } from '../../../helpers/adminLog';
import AdminActionLogModal from '../../log/AdminActionLogModal';

interface FolderGroup {
    group_id: string;
    group_name: string;
    sid: string;
    description: string;
    is_active: boolean;
    created_by: string;
    created_on: string;
}

interface FolderGroupMember {
    group_id: string;
    node_code: string;
    auth_ids: string;
    sid: string;
    folder_name: string;
}

interface GroupResponse {
    total_count: number;
    result_list: FolderGroup[];
}

interface MemberResponse {
    total_count: number;
    result_list: FolderGroupMember[];
}

interface SelectOption {
    value: string;
    label: string;
}

const AUTH_OPTIONS: SelectOption[] = [
    { value: '01', label: '01 - Upload File' },
    { value: '02', label: '02 - Change Information' },
    { value: '03', label: '03 - View File' },
    { value: '04', label: '04 - Delete File' },
    { value: '05', label: '05 - Copy Link' },
    { value: '06', label: '06 - Move Link' },
];

const EMPTY_GROUP: FolderGroup = { group_id: '', group_name: '', sid: '', description: '', is_active: true, created_by: '', created_on: '' };
const EMPTY_MEMBER: FolderGroupMember = { group_id: '', node_code: '', auth_ids: '', sid: '', folder_name: '' };

const selectMenuStyle = { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) };

function FolderGroupManagement() {
    const [groups, setGroups] = useState<FolderGroup[]>([]);
    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
    const [isGroupEdit, setIsGroupEdit] = useState(false);
    const [groupForm, setGroupForm] = useState<FolderGroup>(EMPTY_GROUP);

    const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<FolderGroup | null>(null);
    const [members, setMembers] = useState<FolderGroupMember[]>([]);
    const [memberForm, setMemberForm] = useState<FolderGroupMember>(EMPTY_MEMBER);
    const [isMemberFormOpen, setIsMemberFormOpen] = useState(false);
    const [isMemberEdit, setIsMemberEdit] = useState(false);
    const [memberAuthSelected, setMemberAuthSelected] = useState<SelectOption[]>([]);

    const [searchName, setSearchName] = useState('');
    const [searchSid, setSearchSid] = useState('');
    const [activeFilter, setActiveFilter] = useState({ name: '', sid: '' });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [isLogOpen, setIsLogOpen] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const companies: CompanyMaster[] = useSelector<ApplicationState, CompanyMaster[]>(
        state => state.companyMaster?.responseCompanyMaster?.result_list ?? []
    );
    const { userRole } = useAdminContext();

    const companyOptions: SelectOption[] = useMemo(() => {
        if (companies.length === 0 || userRole.length === 0) return [];
        const hasMasterSid = userRole.some(r => r.sid === '*');
        if (hasMasterSid) return companies.map(c => ({ value: c.sid, label: c.company_name }));
        const allowedSids = new Set(userRole.map(r => r.sid));
        return companies.filter(c => allowedSids.has(c.sid)).map(c => ({ value: c.sid, label: c.company_name }));
    }, [companies, userRole]);

    const isCompanyLocked = companyOptions.length === 1;

    const findCompanyName = useCallback((sid: string) => {
        const found = companies.find(c => c.sid === sid);
        return found ? found.company_name : sid;
    }, [companies]);

    const token = () => localStorage.getItem('DMS_APPLICATION') ?? '';

    const fetchGroups = useCallback(async (sid = '') => {
        try {
            const res = await fetch(`/v1/folder-group?sid=${sid}`, {
                headers: { Authorization: `Bearer ${token()}` },
            });
            if (!res.ok) throw await res.json();
            const data: GroupResponse = await res.json();
            setGroups(data.result_list ?? []);
        } catch (err) {
            Swal.fire('Error', (err as any).message ?? 'โหลดข้อมูลไม่สำเร็จ', 'error');
        }
    }, []);

    const fetchMembers = useCallback(async (group_id: string) => {
        try {
            const res = await fetch(`/v1/folder-group-member?group_id=${group_id}`, {
                headers: { Authorization: `Bearer ${token()}` },
            });
            if (!res.ok) throw await res.json();
            const data: MemberResponse = await res.json();
            setMembers(data.result_list ?? []);
        } catch (err) {
            Swal.fire('Error', (err as any).message ?? 'โหลดข้อมูลไม่สำเร็จ', 'error');
        }
    }, []);

    useEffect(() => {
        if (companyOptions.length === 1) {
            setSearchSid(companyOptions[0].value);
            setActiveFilter(prev => ({ ...prev, sid: companyOptions[0].value }));
        }
    }, [companyOptions]);

    const filteredGroups = useMemo(() => {
        return groups.filter(g => {
            if (activeFilter.name && !g.group_name.toLowerCase().includes(activeFilter.name.toLowerCase())) return false;
            if (activeFilter.sid && g.sid !== activeFilter.sid) return false;
            return true;
        });
    }, [groups, activeFilter]);

    const totalPages = Math.ceil(filteredGroups.length / pageSize);
    const pagedGroups = filteredGroups.slice((page - 1) * pageSize, page * pageSize);

    const handleSearch = async () => {
        await fetchGroups(searchSid);
        setActiveFilter({ name: searchName, sid: searchSid });
        setPage(1);
        setHasSearched(true);
    };

    // ── Group CRUD ──────────────────────────────────────────────

    const openCreateGroup = () => {
        const sid = isCompanyLocked ? companyOptions[0].value : searchSid;
        setGroupForm({ ...EMPTY_GROUP, sid });
        setIsGroupEdit(false);
        setIsGroupModalOpen(true);
    };

    const openEditGroup = (g: FolderGroup) => {
        setGroupForm({ ...g });
        setIsGroupEdit(true);
        setIsGroupModalOpen(true);
    };

    const handleGroupConfirm = async () => {
        if (!groupForm.group_name.trim() || !groupForm.sid.trim()) {
            Swal.fire('กรุณากรอกข้อมูลให้ครบ', '', 'warning');
            return;
        }
        try {
            const res = await fetch('/v1/folder-group', {
                method: isGroupEdit ? 'PUT' : 'POST',
                headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(groupForm),
            });
            if (!res.ok) throw await res.json();
            const data: GroupResponse = await res.json();
            setGroups(data.result_list ?? []);
            setIsGroupModalOpen(false);
            logAdminAction('ADM-DMS102', isGroupEdit ? 'UPDATE_GROUP' : 'CREATE_GROUP', groupForm.group_id || '', groupForm.sid, `group_name=${groupForm.group_name}`);
            Swal.fire({ title: 'Success', icon: 'success', timer: 1500, showConfirmButton: false });
        } catch (err) {
            Swal.fire('Error', (err as any).message ?? 'บันทึกไม่สำเร็จ', 'error');
        }
    };

    const handleDeleteGroup = (g: FolderGroup) => {
        Swal.fire({
            title: 'ยืนยันการลบกลุ่ม?',
            text: `${g.group_name} (จะลบ folder members และ user mappings ทั้งหมดในกลุ่มนี้ด้วย)`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'ลบ',
            cancelButtonText: 'ยกเลิก',
        }).then(async result => {
            if (!result.isConfirmed) return;
            try {
                const res = await fetch(
                    `/v1/folder-group?group_id=${encodeURIComponent(g.group_id)}&sid=${encodeURIComponent(g.sid)}`,
                    { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } }
                );
                if (!res.ok) throw await res.json();
                const data: GroupResponse = await res.json();
                setGroups(data.result_list ?? []);
                logAdminAction('ADM-DMS102', 'DELETE_GROUP', g.group_id, g.sid);
                Swal.fire({ title: 'Deleted!', icon: 'success', timer: 1500, showConfirmButton: false });
            } catch (err) {
                Swal.fire('Error', (err as any).message ?? 'ลบไม่สำเร็จ', 'error');
            }
        });
    };

    // ── Member CRUD ─────────────────────────────────────────────

    const openMemberModal = async (g: FolderGroup) => {
        setSelectedGroup(g);
        await fetchMembers(g.group_id);
        setIsMemberModalOpen(true);
    };

    const openAddMember = () => {
        setMemberForm({ ...EMPTY_MEMBER, group_id: selectedGroup!.group_id, sid: selectedGroup!.sid });
        setMemberAuthSelected([]);
        setIsMemberEdit(false);
        setIsMemberFormOpen(true);
    };

    const openEditMember = (m: FolderGroupMember) => {
        setMemberForm({ ...m });
        const selectedAuths = m.auth_ids.split('|').map(a => AUTH_OPTIONS.find(o => o.value === a.trim())).filter(Boolean) as SelectOption[];
        setMemberAuthSelected(selectedAuths);
        setIsMemberEdit(true);
        setIsMemberFormOpen(true);
    };

    const handleMemberConfirm = async () => {
        const authIds = memberAuthSelected.map(a => a.value).sort().join('|');
        if (!memberForm.node_code.trim() || !authIds) {
            Swal.fire('กรุณากรอกข้อมูลให้ครบ', '', 'warning');
            return;
        }
        const payload = { ...memberForm, auth_ids: authIds };
        try {
            const res = await fetch('/v1/folder-group-member', {
                method: isMemberEdit ? 'PUT' : 'POST',
                headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw await res.json();
            const data: MemberResponse = await res.json();
            setMembers(data.result_list ?? []);
            setIsMemberFormOpen(false);
            logAdminAction('ADM-DMS102', isMemberEdit ? 'UPDATE_MEMBER' : 'ADD_MEMBER', memberForm.group_id, memberForm.sid, `node_code=${memberForm.node_code}, auth_ids=${authIds}`);
            Swal.fire({ title: 'Success', icon: 'success', timer: 1500, showConfirmButton: false });
        } catch (err) {
            Swal.fire('Error', (err as any).message ?? 'บันทึกไม่สำเร็จ', 'error');
        }
    };

    const handleMultiMemberConfirm = async (
        selections: { node_code: string; folder_name: string }[],
        authIds: string
    ) => {
        try {
            for (const sel of selections) {
                const payload = {
                    group_id: selectedGroup!.group_id,
                    node_code: sel.node_code,
                    auth_ids: authIds,
                    sid: selectedGroup!.sid,
                    folder_name: sel.folder_name,
                };
                const res = await fetch('/v1/folder-group-member', {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                if (!res.ok) throw await res.json();
            }
            await fetchMembers(selectedGroup!.group_id);
            setIsMemberFormOpen(false);
            logAdminAction('ADM-DMS102', 'ADD_MEMBER', selectedGroup!.group_id, selectedGroup!.sid,
                `nodes=${JSON.stringify(selections.map(s => s.node_code))}, auth_ids=${authIds}`);
            Swal.fire({ title: 'Success', icon: 'success', timer: 1500, showConfirmButton: false });
        } catch (err) {
            Swal.fire('Error', (err as any).message ?? 'บันทึกไม่สำเร็จ', 'error');
        }
    };

    const handleDeleteMember = (m: FolderGroupMember) => {
        Swal.fire({
            title: 'ยืนยันการลบ?',
            text: m.folder_name || m.node_code,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'ลบ',
            cancelButtonText: 'ยกเลิก',
        }).then(async result => {
            if (!result.isConfirmed) return;
            try {
                const res = await fetch(
                    `/v1/folder-group-member?group_id=${encodeURIComponent(m.group_id)}&node_code=${encodeURIComponent(m.node_code)}`,
                    { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } }
                );
                if (!res.ok) throw await res.json();
                const data: MemberResponse = await res.json();
                setMembers(data.result_list ?? []);
                logAdminAction('ADM-DMS102', 'DELETE_MEMBER', m.group_id, m.sid, `node_code=${m.node_code}`);
                Swal.fire({ title: 'Deleted!', icon: 'success', timer: 1500, showConfirmButton: false });
            } catch (err) {
                Swal.fire('Error', (err as any).message ?? 'ลบไม่สำเร็จ', 'error');
            }
        });
    };

    return (
        <>
            <div className="mt-2">
                <div className="trapezoid" style={{ width: 'auto', display: 'inline-block' }}>
                    <span className="metadata-font">ADM-DMS102 Folder Group Management</span>
                </div>
                <div style={{ display: 'inline', float: 'right' }}>
                    <ChakraButton
                        colorScheme="purple"
                        size="sm"
                        leftIcon={<FaHistory />}
                        className="mx-1"
                        onClick={() => setIsLogOpen(true)}
                    >
                        Log
                    </ChakraButton>
                    <ChakraButton
                        colorScheme="green"
                        size="sm"
                        leftIcon={<FaPlusCircle />}
                        className="mx-1"
                        onClick={openCreateGroup}
                    >
                        Create Group
                    </ChakraButton>
                </div>
            </div>

            <AdminActionLogModal
                isOpen={isLogOpen}
                onClose={() => setIsLogOpen(false)}
                sourceModule="ADM-DMS102"
                defaultSid={searchSid}
            />

            <div className="mat-box employee-font font-size-employee">
                <Row>
                    <Col>
                        <Row className="trapezoid trapezoid-purple" style={{ margin: 'initial' }}>
                            <span style={{ fontSize: '15px' }}>Search Menu</span>
                        </Row>
                        <Card.Body
                            className="mat-box shadow"
                            style={{ borderColor: '#e5e6e9 #dfe0e4 #d0d1d5', borderStyle: 'solid', borderWidth: '1px' }}
                        >
                            <Row>
                                <Col xs={5}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>ชื่อกลุ่ม :</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="ค้นหาชื่อกลุ่ม"
                                            value={searchName}
                                            onChange={e => setSearchName(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={5}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>บริษัท :</Form.Label>
                                        <Select
                                            menuPortalTarget={document.body}
                                            styles={selectMenuStyle}
                                            placeholder="เลือก บริษัท"
                                            options={companyOptions}
                                            value={companyOptions.find(c => c.value === searchSid) ?? null}
                                            onChange={opt => setSearchSid(opt?.value ?? '')}
                                            isClearable={!isCompanyLocked}
                                            isDisabled={isCompanyLocked}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={2} className="d-flex align-items-end pb-3">
                                    <ChakraButton
                                        leftIcon={<FaSearch />}
                                        colorScheme="green"
                                        size="sm"
                                        onClick={handleSearch}
                                    >
                                        ค้นหา
                                    </ChakraButton>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Col>
                </Row>

                <br />

                <Row>
                    <Col xs={6}>
                        <div className="d-flex justify-content-start my-2 my-lg-0 align-items-center">
                            <label style={{ fontSize: '14px', marginBottom: 0 }}>Show&nbsp;&nbsp;</label>
                            <select
                                className="form-control form-control-sm d-inline-block"
                                style={{ width: 'auto' }}
                                value={pageSize}
                                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                            <label style={{ fontSize: '14px', marginBottom: 0 }}>&nbsp;&nbsp;entries</label>
                        </div>
                    </Col>
                    <Col xs={6}>
                        <div className="d-flex justify-content-end my-2 my-lg-0">
                            <Pagination
                                count={totalPages}
                                shape="rounded"
                                showFirstButton
                                showLastButton
                                page={page}
                                onChange={(_, value) => setPage(value)}
                            />
                        </div>
                    </Col>
                </Row>

                <Row className="mt-2">
                    <Col>
                        <table className="table table-bordered align-middle employee-font font-size-employee">
                            <thead>
                                <tr className="text-center table-active thead-light">
                                    <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Action</th>
                                    <th>ชื่อกลุ่ม</th>
                                    <th>บริษัท</th>
                                    <th>คำอธิบาย</th>
                                    <th style={{ width: '80px' }}>Active</th>
                                    <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Folders</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!hasSearched ? (
                                    <tr><td colSpan={6} style={{ background: '#b7b7a4', color: 'black', fontSize: '10pt', textAlign: 'center' }}>กรุณากด ค้นหา เพื่อแสดงข้อมูล</td></tr>
                                ) : pagedGroups.length === 0 ? (
                                    <tr><td colSpan={6} style={{ background: '#b7b7a4', color: 'black', fontSize: '10pt', textAlign: 'center' }}>No Data.</td></tr>
                                ) : (
                                    pagedGroups.map((g, i) => (
                                        <tr key={i}>
                                            <td className="text-center">
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    <IconButton
                                                        variant="outline"
                                                        colorScheme="blue"
                                                        size="sm"
                                                        icon={<FaEdit />}
                                                        aria-label="Edit"
                                                        onClick={() => openEditGroup(g)}
                                                    />
                                                    <IconButton
                                                        variant="outline"
                                                        colorScheme="red"
                                                        size="sm"
                                                        icon={<FaTimesCircle />}
                                                        aria-label="Delete"
                                                        onClick={() => handleDeleteGroup(g)}
                                                    />
                                                </div>
                                            </td>
                                            <td>{g.group_name}</td>
                                            <td>{findCompanyName(g.sid)}</td>
                                            <td>{g.description}</td>
                                            <td className="text-center">
                                                <span className={`badge ${g.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                                    {g.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <IconButton
                                                    variant="outline"
                                                    colorScheme="teal"
                                                    size="sm"
                                                    icon={<FaFolderOpen />}
                                                    aria-label="Manage Folders"
                                                    onClick={() => openMemberModal(g)}
                                                />
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </Col>
                </Row>
            </div>

            <Modal isOpen={isGroupModalOpen} toggle={() => setIsGroupModalOpen(false)}>
                <ModalHeader toggle={() => setIsGroupModalOpen(false)}>
                    {isGroupEdit ? 'Edit Group' : 'Create Group'}
                </ModalHeader>
                <ModalBody>
                    <div className="form-group">
                        <label>ชื่อกลุ่ม <span style={{ color: 'red' }}>*</span></label>
                        <input
                            type="text"
                            className="form-control"
                            value={groupForm.group_name}
                            onChange={e => setGroupForm(prev => ({ ...prev, group_name: e.target.value }))}
                            placeholder="ระบุชื่อกลุ่ม"
                        />
                    </div>
                    <div className="form-group mt-2">
                        <label>บริษัท <span style={{ color: 'red' }}>*</span></label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectMenuStyle}
                            placeholder="เลือก บริษัท"
                            options={companyOptions}
                            value={companyOptions.find(c => c.value === groupForm.sid) ?? null}
                            onChange={opt => setGroupForm(prev => ({ ...prev, sid: opt?.value ?? '' }))}
                            isDisabled={isGroupEdit || isCompanyLocked}
                        />
                    </div>
                    <div className="form-group mt-2">
                        <label>คำอธิบาย</label>
                        <textarea
                            className="form-control"
                            rows={3}
                            value={groupForm.description}
                            onChange={e => setGroupForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="คำอธิบายกลุ่ม (ไม่บังคับ)"
                        />
                    </div>
                    {isGroupEdit && (
                        <div className="form-group mt-2">
                            <label>สถานะ</label>
                            <select
                                className="form-control"
                                value={groupForm.is_active ? 'true' : 'false'}
                                onChange={e => setGroupForm(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleGroupConfirm}>{isGroupEdit ? 'Save' : 'Create'}</Button>
                    <ChakraButton colorScheme="red" onClick={() => setIsGroupModalOpen(false)}>Cancel</ChakraButton>
                </ModalFooter>
            </Modal>

            <Modal isOpen={isMemberModalOpen} toggle={() => setIsMemberModalOpen(false)} size="lg">
                <ModalHeader toggle={() => setIsMemberModalOpen(false)}>
                    Folder Members — {selectedGroup?.group_name}
                </ModalHeader>
                <ModalBody>
                    <div className="mb-2 text-right">
                        <ChakraButton size="sm" colorScheme="green" leftIcon={<FaPlusCircle />} onClick={openAddMember}>
                            เพิ่ม Folder
                        </ChakraButton>
                    </div>
                    <table className="table table-bordered table-sm">
                        <thead className="thead-light">
                            <tr>
                                <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Action</th>
                                <th>Folder Name</th>
                                <th>Node Code</th>
                                <th>Auth IDs</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.length === 0 ? (
                                <tr><td colSpan={4} className="text-center">ยังไม่มี folder ในกลุ่มนี้</td></tr>
                            ) : (
                                members.map((m, i) => (
                                    <tr key={i}>
                                        <td className="text-center">
                                            <div className="flex gap-1 justify-around">
                                                <IconButton
                                                    variant="outline"
                                                    colorScheme="blue"
                                                    size="sm"
                                                    icon={<FaEdit />}
                                                    aria-label="Edit"
                                                    onClick={() => openEditMember(m)}
                                                />
                                                <IconButton
                                                    variant="outline"
                                                    colorScheme="red"
                                                    size="sm"
                                                    icon={<FaTimesCircle />}
                                                    aria-label="Delete"
                                                    onClick={() => handleDeleteMember(m)}
                                                />
                                            </div>
                                        </td>
                                        <td>{m.folder_name || '-'}</td>
                                        <td><code>{m.node_code}</code></td>
                                        <td>{m.auth_ids}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {isMemberFormOpen && !isMemberEdit && (
                        <FolderTreePicker
                            sid={selectedGroup!.sid}
                            addedNodeCodes={members.map(m => m.node_code)}
                            onConfirm={handleMultiMemberConfirm}
                            onCancel={() => setIsMemberFormOpen(false)}
                            companyShortName={companies.find(c => c.sid === (selectedGroup!.sid === '*' ? activeFilter.sid : selectedGroup!.sid))?.company_short_name}
                        />
                    )}

                    {isMemberFormOpen && isMemberEdit && (
                        <div className="border p-3 mt-3 rounded bg-light">
                            <h6>Edit Auth IDs</h6>
                            <div className="form-group">
                                <label>Auth IDs <span style={{ color: 'red' }}>*</span></label>
                                <Select
                                    menuPortalTarget={document.body}
                                    styles={selectMenuStyle}
                                    isMulti
                                    placeholder="เลือกสิทธิ์"
                                    options={AUTH_OPTIONS}
                                    value={memberAuthSelected}
                                    onChange={val => setMemberAuthSelected(val as SelectOption[])}
                                />
                            </div>
                            <div className="mt-2" style={{ display: 'flex', gap: 8 }}>
                                <Button size="sm" color="primary" onClick={handleMemberConfirm}>Save</Button>
                                <ChakraButton size="sm" colorScheme="red" onClick={() => setIsMemberFormOpen(false)}>Cancel</ChakraButton>
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <ChakraButton colorScheme="red" onClick={() => setIsMemberModalOpen(false)}>Close</ChakraButton>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default FolderGroupManagement;
